import React from 'react';

/**
 * Full-screen (or container-scoped) overlay shown during image uploads.
 * Prevents user interaction and communicates that work is in progress.
 *
 * Props:
 *   visible   — boolean, show/hide
 *   message   — string, e.g. "Uploading image…"
 *   subtext   — optional secondary line, e.g. "Scanning for content safety"
 *   fullScreen — boolean (default true). When false, overlays the nearest positioned parent.
 *   progress  — optional { current, total } for multi-file uploads
 */
const UploadOverlay = ({ visible, message, subtext, fullScreen = true, progress }) => {
  if (!visible) return null;

  const hasProgress = progress && progress.total > 0;
  const pct = hasProgress ? Math.round((progress.current / progress.total) * 100) : 0;
  const isMulti = hasProgress && progress.total > 1;

  const containerStyle = {
    position: fullScreen ? 'fixed' : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.55)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    backdropFilter: 'blur(2px)',
  };

  const cardStyle = {
    background: '#fff',
    borderRadius: '16px',
    padding: '32px 40px',
    textAlign: 'center',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25)',
    maxWidth: '380px',
    width: '90%',
  };

  const spinnerStyle = {
    width: '44px',
    height: '44px',
    border: '4px solid #e2e8f0',
    borderTop: '4px solid #2563eb',
    borderRadius: '50%',
    animation: 'uploadOverlaySpin 0.8s linear infinite',
    margin: '0 auto 20px',
  };

  const messageStyle = {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1a202c',
    marginBottom: '8px',
  };

  const subtextStyle = {
    fontSize: '13px',
    color: '#718096',
    lineHeight: 1.4,
    marginBottom: hasProgress ? '16px' : 0,
  };

  // Progress bar track
  const trackStyle = {
    width: '100%',
    height: '8px',
    background: '#e2e8f0',
    borderRadius: '4px',
    overflow: 'hidden',
    marginBottom: '8px',
  };

  // Determinate fill (multi-file)
  const fillStyle = {
    width: `${pct}%`,
    height: '100%',
    background: 'linear-gradient(90deg, #2563eb, #3b82f6)',
    borderRadius: '4px',
    transition: 'width 0.4s ease',
  };

  // Indeterminate shimmer (single-file)
  const shimmerStyle = {
    width: '40%',
    height: '100%',
    background: 'linear-gradient(90deg, #2563eb, #3b82f6)',
    borderRadius: '4px',
    animation: 'uploadOverlayShimmer 1.4s ease-in-out infinite',
  };

  const countStyle = {
    fontSize: '13px',
    color: '#2563eb',
    fontWeight: 600,
  };

  return (
    <>
      <style>{`
        @keyframes uploadOverlaySpin {
          to { transform: rotate(360deg); }
        }
        @keyframes uploadOverlayShimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(350%); }
        }
      `}</style>
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={spinnerStyle} />
          <div style={messageStyle}>{message || 'Uploading…'}</div>
          {subtext && <div style={subtextStyle}>{subtext}</div>}

          {/* Progress bar — always shown */}
          <div style={trackStyle}>
            {isMulti ? (
              <div style={fillStyle} />
            ) : (
              <div style={shimmerStyle} />
            )}
          </div>

          {/* File count for multi-file uploads */}
          {isMulti && (
            <div style={countStyle}>
              {progress.current} of {progress.total} &mdash; {pct}%
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default UploadOverlay;
